import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { QuizQuestion } from "@/features/training/components/track-quiz"

import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { TrackQuiz } from "@/features/training/components/track-quiz"
import { useGetTrainingModules } from "@/features/training/hooks/use-get-training-modules"
import { useUpdateModuleStatus } from "@/features/training/hooks/use-update-module-status"
import { fireTrackCompletionConfetti } from "@/features/training/utils/confetti"

const QUIZ_SECTION_ID = "quiz-everyday-ai"

export function EverydayAIQuizPage() {
  const { t } = useTranslation("", { keyPrefix: "training.quiz.everyday_ai" })
  const navigate = useNavigate()
  const updateModuleStatus = useUpdateModuleStatus()
  const modulesQuery = useGetTrainingModules()

  // Pool of 9 questions — 4 are randomly selected each attempt
  const allQuestions: QuizQuestion[] = [
    {
      id: "q1",
      question: t("q1.question", "What is the best practice when writing prompts for AI Explorer?"),
      options: [
        { id: "a", label: t("q1.a", "Keep prompts as short as possible, one word is best") },
        { id: "b", label: t("q1.b", "Be specific and provide context about what you need") },
        {
          id: "c",
          label: t("q1.c", "Always write prompts in English regardless of your language"),
        },
        { id: "d", label: t("q1.d", "Copy and paste entire documents into the prompt") },
      ],
      correctId: "b",
    },
    {
      id: "q2",
      question: t("q2.question", "What can AI Flows help you automate?"),
      options: [
        { id: "a", label: t("q2.a", "Only email responses") },
        { id: "b", label: t("q2.b", "Repetitive multi-step workflows using AI") },
        { id: "c", label: t("q2.c", "Hardware configuration") },
        { id: "d", label: t("q2.d", "Network security monitoring") },
      ],
      correctId: "b",
    },
    {
      id: "q3",
      question: t("q3.question", "What is AI Parser primarily used for?"),
      options: [
        { id: "a", label: t("q3.a", "Creating images and videos") },
        { id: "b", label: t("q3.b", "Translating documents between languages") },
        { id: "c", label: t("q3.c", "Extracting structured data from documents") },
        { id: "d", label: t("q3.d", "Writing code") },
      ],
      correctId: "c",
    },
    {
      id: "q4",
      question: t(
        "q4.question",
        "What should you always do after receiving an AI-generated response?"
      ),
      options: [
        { id: "a", label: t("q4.a", "Accept it without question -- AI is always right") },
        { id: "b", label: t("q4.b", "Delete it and start over") },
        { id: "c", label: t("q4.c", "Review and verify the information before using it") },
        { id: "d", label: t("q4.d", "Share it immediately with colleagues") },
      ],
      correctId: "c",
    },
    {
      id: "q5",
      question: t("q5.question", "What is Studio AI best suited for?"),
      options: [
        { id: "a", label: t("q5.a", "Writing complex code") },
        { id: "b", label: t("q5.b", "Creating images and visual content with AI") },
        { id: "c", label: t("q5.c", "Managing employee schedules") },
        { id: "d", label: t("q5.d", "Running database queries") },
      ],
      correctId: "b",
    },
    {
      id: "q6",
      question: t("q6.question", "What is a key advantage of using Projects in AI Explorer?"),
      options: [
        { id: "a", label: t("q6.a", "Projects automatically write reports for you") },
        {
          id: "b",
          label: t(
            "q6.b",
            "Projects let you organize context and documents for more focused AI conversations"
          ),
        },
        { id: "c", label: t("q6.c", "Projects replace the need for any file storage") },
        { id: "d", label: t("q6.d", "Projects are only available to administrators") },
      ],
      correctId: "b",
    },
    {
      id: "q7",
      question: t("q7.question", "What does AI Translator help you do?"),
      options: [
        { id: "a", label: t("q7.a", "Only translate between English and Spanish") },
        {
          id: "b",
          label: t("q7.b", "Translate documents and text between multiple languages using AI"),
        },
        { id: "c", label: t("q7.c", "Automatically publish translated content online") },
        { id: "d", label: t("q7.d", "Convert audio files to text") },
      ],
      correctId: "b",
    },
    {
      id: "q8",
      question: t(
        "q8.question",
        "When is it most appropriate to use the Research Agent in AI Explorer?"
      ),
      options: [
        { id: "a", label: t("q8.a", "For simple yes/no questions") },
        {
          id: "b",
          label: t(
            "q8.b",
            "When you need comprehensive research across multiple sources on a complex topic"
          ),
        },
        { id: "c", label: t("q8.c", "Only for translating documents") },
        { id: "d", label: t("q8.d", "To create images and videos") },
      ],
      correctId: "b",
    },
    {
      id: "q9",
      question: t("q9.question", "What is an AI Agent?"),
      options: [
        { id: "a", label: t("q9.a", "A human who works with AI") },
        { id: "b", label: t("q9.b", "A chatbot that can only answer questions") },
        {
          id: "c",
          label: t(
            "q9.c",
            "An AI system that can perform tasks autonomously by using tools and making decisions"
          ),
        },
        { id: "d", label: t("q9.d", "A type of computer hardware") },
      ],
      correctId: "c",
    },
  ]

  const handleComplete = useCallback(async () => {
    const existing = modulesQuery.data?.modules.find((m) => m.module_id === QUIZ_SECTION_ID)
    if (!existing || existing.status !== "completed") {
      await updateModuleStatus.mutateAsync({
        moduleId: QUIZ_SECTION_ID,
        data: { status: "completed", completed_at: new Date().toISOString() },
      })
    }

    fireTrackCompletionConfetti()

    setTimeout(() => {
      void navigate("/training")
    }, 2500)
  }, [modulesQuery.data, updateModuleStatus, navigate])

  return (
    <CourseLayout>
      <TrackQuiz
        questions={allQuestions}
        trackName="Everyday AI Users"
        onComplete={handleComplete}
      />
    </CourseLayout>
  )
}
